/**
 * The Profile tab's screen (shared-client-screens D2, rows P1 to P3, S1): the app's Profile drawn
 * the same in the Mini App and on the phone website. It owns the loading and error screens, the
 * edit sheet (a save says "Your info is saved.") and the card-removal sheet, and puts the header
 * in the shell's header slot. Props only: each side passes its own reads and writes, Back, and its
 * slots (the app's legal links open outside the Mini App; the website adds Sign out).
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/profile-tab.html (round 3b).
 */
import { useLayoutEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { useToast } from '@/components/Toast/ToastContext';
import type { ProfilePayload, ProfileUpdateInput, SavedCard } from '@/types/profile';
import { ProfileEditSheet } from './ProfileEditSheet';
import { ProfileHeader, ProfileView, RemoveCardSheet, type EditableProfileField, type ProfileViewProps } from './ProfileView';
import styles from './ProfilePage.module.css';

export interface ProfileScreenProps {
  /** Back to Home (P1). */
  onBack: () => void;
  /** The shell's header slot (ClientShellContext.setHeaderSlot). */
  setHeaderSlot: (node: ReactNode) => void;
  /** The profile read (S1). */
  profile: { status: 'loading' | 'error' | 'ready'; data: ProfilePayload | undefined; retry: () => void };
  cards: ProfileViewProps['cards'];
  /** Saves My info; rejects on failure, which keeps the sheet open with its error. */
  save: (values: ProfileUpdateInput) => Promise<unknown>;
  saving: boolean;
  saveError: boolean;
  resetSave: () => void;
  /** Removes a saved card; rejects on failure, which keeps the sheet open with its error. */
  removeCard: (cardId: string) => Promise<unknown>;
  removing: boolean;
  removeError: boolean;
  resetRemove: () => void;
  /** A legal link's click (P3): the app opens it outside the Mini App; left out, a new tab. */
  onLegalLink?: (event: MouseEvent, path: string) => void;
  /** Its own card under Preferences (P2: the website's Sign out). */
  afterPreferences?: ReactNode;
}

export function ProfileScreen({
  onBack,
  setHeaderSlot,
  profile,
  cards,
  save,
  saving,
  saveError,
  resetSave,
  removeCard,
  removing,
  removeError,
  resetRemove,
  onLegalLink,
  afterPreferences,
}: ProfileScreenProps) {
  const { showToast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [editField, setEditField] = useState<EditableProfileField>('firstName');
  const [cardToRemove, setCardToRemove] = useState<SavedCard | null>(null);

  // The page header in the shell's (non-scrolling) header slot.
  useLayoutEffect(() => {
    setHeaderSlot(<ProfileHeader onBack={onBack} />);
    return () => setHeaderSlot(null);
  }, [setHeaderSlot, onBack]);

  if (profile.status === 'loading') {
    return (
      <div className={styles.stateWrap} data-testid="profile-loading">
        <div className={styles.spinner} />
      </div>
    );
  }
  if (profile.status === 'error' || !profile.data) {
    return (
      <div className={styles.stateWrap} data-testid="profile-error">
        <div className={styles.stateTitle}>Couldn’t load your profile</div>
        <button type="button" className={styles.stateBtn} onClick={profile.retry}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <ProfileView
      profile={profile.data}
      cards={cards}
      onEdit={(field) => {
        setEditField(field);
        setEditOpen(true);
      }}
      onRemoveCard={setCardToRemove}
      onLegalLink={onLegalLink}
      afterPreferences={afterPreferences}
    >
      <ProfileEditSheet
        open={editOpen}
        initialField={editField}
        initial={profile.data.myInfo}
        saving={saving}
        error={saveError}
        // Resolves on success (the sheet closes) and REJECTS on failure: the edit sheet catches
        // the rejection, stays open, and shows the inline error.
        onSave={async (values) => {
          await save(values);
          setEditOpen(false);
          showToast('Your info is saved.', 'success');
        }}
        onClose={() => {
          resetSave();
          setEditOpen(false);
        }}
      />

      <RemoveCardSheet
        open={!!cardToRemove}
        card={cardToRemove}
        removing={removing}
        error={removeError}
        onConfirm={async () => {
          if (!cardToRemove) return;
          await removeCard(cardToRemove.id);
          setCardToRemove(null);
        }}
        onClose={() => {
          resetRemove();
          setCardToRemove(null);
        }}
      />
    </ProfileView>
  );
}
